import { useEffect, useState } from "react";
import api from "../services/api";
import { Trophy, Loader2, Medal } from "lucide-react";

const PERIOD_LABEL = { week: "This week", month: "This month", all: "All time" };

export default function Leaderboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/leaderboard/referrals", { params: { period } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const rankColor = (rank) => {
    if (rank === 1) return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (rank === 2) return "bg-slate-100 text-slate-700 border-slate-200";
    if (rank === 3) return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-600 border-slate-100";
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="leaderboard-page">
      <div className="animate-fadeInUp flex items-end justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Referral Leaderboard</h1>
          <p className="text-sm text-slate-500 mt-1">Top referrers earn bonus rewards each month.</p>
        </div>
        <div className="flex bg-slate-100 rounded-xl p-1" data-testid="leaderboard-period-toggle">
          {["week", "month", "all"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${period === p ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid={`leaderboard-period-${p}`}
            >
              {PERIOD_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Bonus banner */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-100 p-4 flex items-center gap-3" data-testid="bonuses-banner">
        <Trophy className="h-6 w-6 text-amber-500" />
        <div className="text-xs text-slate-700">
          <b>Monthly prizes:</b> #1 = ₹{data?.bonuses?.[0] ?? 200} · #2 = ₹{data?.bonuses?.[1] ?? 100} · #3 = ₹{data?.bonuses?.[2] ?? 50} wallet credit
        </div>
      </div>

      {/* Leaderboard list */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="leaderboard-list">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" />
          </div>
        ) : !data?.leaderboard?.length ? (
          <div className="p-12 text-center">
            <Medal className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No referrals yet for this period</p>
            <p className="text-sm text-slate-400 mt-1">Be the first — share your code and climb to the top!</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {data.leaderboard.map((row) => (
              <li
                key={row.rank}
                className={`px-5 py-3 flex items-center justify-between ${row.is_me ? "bg-orange-50/50" : ""}`}
                data-testid={`leaderboard-row-${row.rank}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-xl border flex items-center justify-center text-sm font-bold ${rankColor(row.rank)}`}>
                    {row.rank}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {row.label}{row.is_me ? <span className="ml-1.5 text-[10px] font-bold text-[#FF6B00] uppercase">(you)</span> : null}
                    </p>
                    <p className="text-xs text-slate-400">
                      {row.referrals} referral{row.referrals === 1 ? "" : "s"}
                      {row.last_completed_at ? ` · last ${new Date(row.last_completed_at).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                </div>
                {row.bonus_inr > 0 && (
                  <span className="text-sm font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                    +₹{row.bonus_inr}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* My row outside top 20 */}
      {data?.my_row && (
        <div className="bg-white rounded-2xl shadow-sm border border-orange-200 p-4 flex items-center justify-between animate-fadeInUp" data-testid="leaderboard-my-row">
          <div>
            <p className="text-xs text-slate-500">Your standing</p>
            <p className="text-sm font-semibold text-slate-900">{data.my_row.referrals} successful referral{data.my_row.referrals === 1 ? "" : "s"} this period</p>
          </div>
          <span className="text-xs text-slate-500">Keep going to crack the top 20!</span>
        </div>
      )}
    </div>
  );
}
